import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Grid, Typography, Box } from "@mui/material";
import { Card } from "./UI";
import { useTheme } from "@emotion/react";

const AnalyticsDashboard = ({ tasks, users }) => {
  const statusData = useMemo(() => {
    const counts = tasks.reduce(
      (acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }),
      {}
    );
    return Object.keys(counts).map((key) => ({
      name: key,
      value: counts[key],
    }));
  }, [tasks]);

  const workloadData = useMemo(() => {
    return users.map((u) => ({
      name: u.name.split(" ")[0],
      tasks: tasks.filter(
        (t) => t.assigneeIds.includes(u.id) && t.status !== "Done"
      ).length,
      hours: tasks
        .filter((t) => t.assigneeIds.includes(u.id) && t.status !== "Done")
        .reduce((sum, t) => sum + t.estimateHours, 0),
    }));
  }, [tasks, users]);

  const COLORS = ["#94a3b8", "#60a5fa", "#f59e0b", "#a855f7", "#10b981"];
  const theme = useTheme();
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <Card sx={{ p: 3, height: "100%" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Task Distribution
          </Typography>
          <Box height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Card sx={{ p: 3, height: "100%" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Workload per User
          </Typography>
          <Box height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={workloadData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <RechartsTooltip
                  cursor={{
                    fill: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  }}
                  contentStyle={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  }}
                />
                <Bar
                  dataKey="tasks"
                  fill="#6366f1"
                  radius={[4, 4, 0, 0]}
                  name="Active Tasks"
                />
                <Bar
                  dataKey="hours"
                  fill="#cbd5e1"
                  radius={[4, 4, 0, 0]}
                  name="Est. Hours"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AnalyticsDashboard;
